import { Box, Button, Container, Paper, Typography } from "@mui/material";
import { DataTable } from "modules/common";
import { useState } from "react";
import FrequentlyAskedQuestionDialog from "./FrequentlyAskedQuestionDialog";
import { useCreateFrequentlyAskedQuestionMutation, useDeleteFrequentlyAskedQuestionMutation, useGetFrequentlyAskedQuestionsQuery, useUpdateFrequentlyAskedQuestionMutation } from "api/adminSlice";
import { useTranslation } from "react-i18next";
import { Add } from "@mui/icons-material";

export default function FrequentlyAskedQuestions() {
  const { t } = useTranslation()

  const [page, setPage] = useState(1)

  //=================Frequently Asked Question API hooks==================
  const {
    data: frequentlyAskedQuestions, isLoading: frequentlyAskedQuestionsLoading, isFetching: frequentlyAskedQuestionsFetching, refetch: refetchFrequentlyAskedQuestions
  } = useGetFrequentlyAskedQuestionsQuery({ page, limit: 100 })

  const [createFrequentlyAskedQuestion,
    {
      isLoading: createFrequentlyAskedQuestionLoading,
      isSuccess: createFrequentlyAskedQuestionIsSuccess,
      error: createFrequentlyAskedQuestionError,
      reset: createReset
    }
  ] = useCreateFrequentlyAskedQuestionMutation()

  const [updateFrequentlyAskedQuestion,
    {
      isLoading: updateFrequentlyAskedQuestionLoading,
      isSuccess: updateFrequentlyAskedQuestionIsSuccess,
      error: updateFrequentlyAskedQuestionError,
      reset: updateReset
    }
  ] = useUpdateFrequentlyAskedQuestionMutation()

  const [deleteFrequentlyAskedQuestion,
    {
      isLoading: deleteFrequentlyAskedQuestionLoading,
      isSuccess: deleteFrequentlyAskedQuestionIsSuccess,
      error: deleteFrequentlyAskedQuestionError,
      reset: deleteReset
    }
  ] = useDeleteFrequentlyAskedQuestionMutation()

  //==========Get and navigate through frequently asked questions==========
  const pageChangeHandler = ({ page }) => {
    if (!frequentlyAskedQuestionsFetching || !frequentlyAskedQuestionsLoading) {
      setPage(page)
    }
  }

  //===============Handle frequently asked question values change==============
  const [selectedFrequentlyAskedQuestion, setSelectedFrequentlyAskedQuestion] = useState({
    question_ar: '',
    answer_ar: '',
    question_en: '',
    answer_en: '',
  })

  const resetSelectedFrequentlyAskedQuestion = () => {
    setSelectedFrequentlyAskedQuestion({
      question_ar: '',
      answer_ar: '',
      question_en: '',
      answer_en: '',
    })
  }

  const handleChange = ({ target: { value, name } }) => {
    setSelectedFrequentlyAskedQuestion(prevState => ({ ...prevState, [name]: value }))
  }

  //==============Sercice point dialog handlers==============
  const [isEditing, setIsEditing] = useState(false)
  const [openFrequentlyAskedQuestionDialog, setOpenFrequentlyAskedQuestionDialog] = useState(false)

  const resetAPIHooks = () => {
    createReset()
    updateReset()
    deleteReset()
  }

  const openDialogForCreate = () => {
    resetAPIHooks()
    setIsEditing(false)
    setOpenFrequentlyAskedQuestionDialog(true)
  }

  const openDialogForUpdate = (data) => {
    resetAPIHooks()
    setIsEditing(true)
    setSelectedFrequentlyAskedQuestion(data)
    setOpenFrequentlyAskedQuestionDialog(true)
  }

  const closeDialogHandler = () => {
    setOpenFrequentlyAskedQuestionDialog(false)
    if (isEditing) {
      resetSelectedFrequentlyAskedQuestion()
    }
    setIsEditing(false)
  }


  //================Frequently Asked Question CRUD handlers===============
  const createFrequentlyAskedQuestionHandler = async (payload) => {
    let response = await createFrequentlyAskedQuestion(payload).unwrap()
    checkFrequentlyAskedQuestionActionSuccess(response)
  }
  const updateFrequentlyAskedQuestionHandler = async (payload) => {
    let response = await updateFrequentlyAskedQuestion(payload).unwrap()
    checkFrequentlyAskedQuestionActionSuccess(response)
  }
  const deleteFrequentlyAskedQuestionHandler = async (id) => {
    let response = await deleteFrequentlyAskedQuestion(id).unwrap()
    checkFrequentlyAskedQuestionActionSuccess(response)
  }
  const checkFrequentlyAskedQuestionActionSuccess = (response) => {
    if (response?.success) {
      refetchFrequentlyAskedQuestions()
      setTimeout(() => {
        setOpenFrequentlyAskedQuestionDialog(false)
        resetSelectedFrequentlyAskedQuestion()
      }, 3000);
    }
  }

  const columns = [
    { field: "question_ar", headerName: "السؤال بالعربى", flex: 1 },
    { field: "answer_ar", headerName: "الإجابة بالعربى", flex: 1 },
    { field: "question_en", renderCell: ({ value }) => <Typography><bdi>{value}</bdi></Typography>, headerName: "The question in English", flex: 1 },
    { field: "answer_en", renderCell: ({ value }) => <Typography><bdi>{value}</bdi></Typography>, headerName: "The answer in English", flex: 1 },
  ];

  return (
    <Container>
      <Box display="flex" justifyContent="space-between" alignItems="center" my={25}>
        <Typography variant='h6'>{t("Add question")}</Typography>
        {/* <SearchTextFiled
          fullWidth
          label={t("Search")}
          size="small"
          value={searchKey ?? ''}
          onChange={onSearchKey}
        /> */}
        <Button
          startIcon={<Add />}
          sx={{ textTransform: "none" }}
          variant="contained"
          onClick={openDialogForCreate}
        >
          {t("Add")}
        </Button>
      </Box>
      <Paper sx={{ height: "80vh" }}>
        <DataTable
          rows={frequentlyAskedQuestions?.docs ?? []}
          columns={columns}
          onPaginationModelChange={pageChangeHandler}
          paginationModel={{ page: page - 1, pageSize: 100 }}
          rowCount={frequentlyAskedQuestions?.totalDocs ?? 0}
          loading={frequentlyAskedQuestionsLoading || frequentlyAskedQuestionsFetching}
          onRowClick={({ row }) => openDialogForUpdate(row)}
        />
      </Paper>
      <FrequentlyAskedQuestionDialog
        openFrequentlyAskedQuestionDialog={openFrequentlyAskedQuestionDialog}
        loading={createFrequentlyAskedQuestionLoading || updateFrequentlyAskedQuestionLoading || deleteFrequentlyAskedQuestionLoading}
        isEditing={isEditing}
        frequentlyAskedQuestion={selectedFrequentlyAskedQuestion}
        handleChange={handleChange}
        onCreateFrequentlyAskedQuestion={createFrequentlyAskedQuestionHandler}
        onUpdateFrequentlyAskedQuestion={updateFrequentlyAskedQuestionHandler}
        onDeleteFrequentlyAskedQuestion={deleteFrequentlyAskedQuestionHandler}
        onCloseDialog={closeDialogHandler}
        error={createFrequentlyAskedQuestionError || updateFrequentlyAskedQuestionError || deleteFrequentlyAskedQuestionError}
        isSuccess={createFrequentlyAskedQuestionIsSuccess || updateFrequentlyAskedQuestionIsSuccess || deleteFrequentlyAskedQuestionIsSuccess}
      />
    </Container>
  )
}